import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from './client';

/**
 * AI re-evaluation read surface (T-0051 / T-0058 / E03-F03).
 *
 * Backstory
 * ---------
 * E03-F03 is entirely event-driven on the write side: the `POST
 * /api/v1/overrides` route emits `override.captured` and the T-0051
 * synthesizer worker drives the `ai_reevaluation_runs` row through its
 * lifecycle (`pending` → `running` → terminal). The TA spec §4 calls
 * out a single read endpoint for diagnostics + audit UI:
 *
 *     GET /api/v1/engagements/{id}/reevaluations/{run_id}
 *
 * Backend status (2026-06-03): the synthesizer worker (T-0051) and the
 * snapshot-side enqueue hook (T-0058) are both shipped — but the diagnostic
 * read endpoint above is NOT yet wired on apps/api. The re-eval tray
 * (T-0113) therefore degrades gracefully on 404 in the same shape as
 * `citation-source-modal.tsx` did before T-0093: it shows the structured
 * scope + tag-strategy hint locally and surfaces the "audit will be
 * available once T-0051 lands the read endpoint" empty state.
 *
 * Why the route+by-override list endpoint
 * ---------------------------------------
 * The override modal cannot pass a `run_id` because the run hasn't been
 * INSERTed at the moment the modal closes — the worker creates the row
 * asynchronously after consuming `override.captured`. So the tray opens
 * with the override_id it just captured and POLLS the "runs by
 * trigger_override_id" surface until a row appears (then transitions to
 * polling the single-run surface for status). Both surfaces are
 * documented here and will route to the same eventual backend endpoint;
 * for v1 the client returns the same graceful 404 → "Tray ready, audit
 * deferred" UX from the citation-source pattern.
 *
 * When the backend endpoint lands, this client + tray need no further
 * change — the existing 404-fallback path stays as the "audit deferred"
 * empty state and the live polling path lights up automatically.
 */

export const ReevalRunSchema = z.object({
  run_id: z.string().uuid(),
  /** `same_register` | `same_source_document` | `cross_module` (spec D-20). */
  scope: z.string(),
  /** `synchronous` | `deferred` — null until the worker resolves it. */
  run_mode: z.string().nullable(),
  strategy: z.string().nullable(),
  outcome: z.string().nullable(),
  status: z.string(),
  // Optional on the wire — empty/missing arrays normalize to [] inside
  // the consumer (`runs[0]?.updated_proposal_ids ?? []`). Keeping the
  // schema input optional avoids the apiClient generic divergence
  // `.default([])` would introduce.
  updated_proposal_ids: z.array(z.string().uuid()).optional(),
  debounce_window_id: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
  failure_reason: z.string().nullable(),
});
export type ReevalRun = z.infer<typeof ReevalRunSchema>;

export const ReevalRunListSchema = z.object({
  runs: z.array(ReevalRunSchema),
});
export type ReevalRunList = z.infer<typeof ReevalRunListSchema>;

/**
 * Resolve a single re-eval run by id — used once the tray has discovered
 * a run id via the by-override list. The eventual backend response shape
 * mirrors spec §4 directly.
 */
export function getReevaluationRun(engagementId: string, runId: string): Promise<ReevalRun> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/reevaluations/${encodeURIComponent(
      runId,
    )}`,
    ReevalRunSchema,
  );
}

/**
 * List re-eval runs for an override — used by the tray to discover the
 * run id the T-0051 synthesizer creates asynchronously. Returns an
 * empty `runs` array while the worker hasn't picked up the override yet;
 * 404 falls through to the graceful-degradation path described in this
 * module's header.
 */
export function listReevaluationsByOverride(
  engagementId: string,
  overrideId: string,
): Promise<ReevalRunList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/reevaluations`,
    ReevalRunListSchema,
    { query: { trigger_override_id: overrideId } },
  );
}

/**
 * Treat a 404 (or `not_found` ApiError code) as the "endpoint not
 * shipped yet OR no run discovered yet" empty state — both are valid for
 * v1. Network or schema-mismatch errors are still surfaced to the
 * caller's `.error` channel so they don't get silently swallowed.
 */
function isMissingEndpoint(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export interface UseReevaluationsForOverrideOptions {
  /** Polling interval while no terminal run has been observed yet. */
  pollIntervalMs?: number;
  /** Skip the network call until the override is committed. */
  enabled?: boolean;
}

/**
 * Poll the by-override list until a terminal run appears. The tray uses
 * the returned `runs[0]` (latest, server-sorted by `created_at desc`)
 * to drive the status pill + scope chip + strategy hint. Polling
 * stops automatically once a terminal-status row is observed.
 *
 * The query swallows 404 into an empty list (`{ runs: [] }`) so the
 * tray's "audit will land when backend ships" copy renders without
 * triggering the global error-boundary.
 */
export function useReevaluationsForOverrideQuery(
  engagementId: string | undefined,
  overrideId: string | undefined,
  opts: UseReevaluationsForOverrideOptions = {},
) {
  const enabledGate = Boolean(engagementId && overrideId);
  const pollMs = opts.pollIntervalMs ?? 1500;
  return useQuery({
    queryKey: ['reevaluations', 'by-override', engagementId, overrideId] as const,
    queryFn: async () => {
      try {
        return await listReevaluationsByOverride(engagementId!, overrideId!);
      } catch (err) {
        if (isMissingEndpoint(err)) {
          return { runs: [] } satisfies ReevalRunList;
        }
        throw err;
      }
    },
    enabled: enabledGate && (opts.enabled ?? true),
    // Poll until we see a terminal-status row (or the user dismisses the
    // tray). The interval is short because the synthesizer typically
    // completes inside the spec's ≤10s p95 same-register budget.
    refetchInterval: (q) => {
      const data = q.state.data as ReevalRunList | undefined;
      if (!data || data.runs.length === 0) return pollMs;
      const latest = data.runs[0];
      const terminal =
        latest?.status === 'no_change' ||
        latest?.status === 'updated' ||
        latest?.status === 'inconsistency_flagged' ||
        latest?.status === 'failed';
      return terminal ? false : pollMs;
    },
    retry: false,
  });
}

/**
 * Map the v1 reason tags (`overrides.ts → OVERRIDE_REASON_TAXONOMY_V1`)
 * onto the strategy hint shown in the re-eval tray. Same router shape as
 * the prototype's `TAG_STRATEGY` map in
 * `design_handoff_caliber_v2/prototype/modals-round4a.js`. The tray uses
 * the resolved row from this map (NOT the LLM) — per CLAUDE.md hard rule
 * #7 ("AI surfaces never produce numbers"), the strategy hint is a pure
 * lookup against the taxonomy key.
 *
 * Strategy keys mirror `packages/ai-reeval/src/router.ts`:
 *   - 're-extract'      : wider retrieval window
 *   - 're-rag'          : re-RAG against latest documents
 *   - 'glossary-update' : update engagement glossary + re-extract
 *   - 'human-flag'      : flag related items for human review
 *   - 'none'            : no re-extraction (override is canonical)
 */
export interface StrategyHint {
  /** Resolved router strategy. */
  strategy: 're-extract' | 're-rag' | 'glossary-update' | 'human-flag' | 'none';
  /** Short label for the hint banner. */
  label: string;
  /** One-line detail copy. */
  detail: string;
}

const STRATEGY_HINTS: Record<string, StrategyHint> = {
  vendor_evidence_inconsistent: {
    strategy: 're-rag',
    label: 'Re-RAG against latest documents',
    detail: 'Pinning to documents indexed after the override timestamp',
  },
  methodology_threshold_override: {
    strategy: 'none',
    label: 'No re-extraction — override is canonical',
    detail: 'Audit trail retained; AI proposals stay below threshold',
  },
  regulatory_requirement: {
    strategy: 'none',
    label: 'No re-extraction — regulatory override',
    detail: 'Audit trail retained; rule supersedes AI',
  },
  domain_expertise_correction: {
    strategy: 're-extract',
    label: 'Re-extract with broader context window',
    detail: 'Wider retrieval (k=12 → k=24) to surface missed context',
  },
  data_quality_issue: {
    strategy: 'human-flag',
    label: 'Flag related items for human review',
    detail: 'No re-extraction; source-data quality requires investigation',
  },
  clarification_with_client: {
    strategy: 'glossary-update',
    label: 'Update engagement glossary + re-extract',
    detail: 'Glossary update propagates to related documents',
  },
  other: {
    strategy: 'none',
    label: 'No re-extraction — override is canonical',
    detail: 'Audit trail retained; rationale carries the reasoning',
  },
};

export function strategyHintForTag(reasonTag: string): StrategyHint {
  return (
    STRATEGY_HINTS[reasonTag] ?? {
      strategy: 'none',
      label: 'No re-extraction — override is canonical',
      detail: 'Audit trail retained',
    }
  );
}

/**
 * UI status the tray shows: the four bands the prototype displays
 * (queued → routing → synthesizing → ready) plus the failure path. We
 * compute the band from the backend row's `status` string so the tray
 * doesn't have to know the worker's terminal-state vocabulary.
 */
export type TrayBand =
  | 'queued'
  | 'routing'
  | 'synthesizing'
  | 'ready'
  | 'failed'
  | 'deferred-cross-module';

export function trayBandForRun(run: ReevalRun | undefined): TrayBand {
  if (!run) return 'queued';
  switch (run.status) {
    case 'pending':
      // Cross-module runs are deferred to the next snapshot capture
      // (E03-F03 §1) — surface that distinctly so the user doesn't wait
      // on a synthesis that won't happen until snapshot.captured fires.
      return run.scope === 'cross_module' ? 'deferred-cross-module' : 'routing';
    case 'running':
      return 'synthesizing';
    case 'no_change':
    case 'updated':
    case 'inconsistency_flagged':
      return 'ready';
    case 'failed':
      return 'failed';
    default:
      return 'queued';
  }
}
